import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ShieldAlert,
  KeyRound,
  CheckCircle2,
  ArrowRight,
  Copy,
  Download,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useSuperAdmin } from "./SuperAdminContext";

export default function SuperAdminSetup() {
  const { setup, setupComplete, authenticated, loading } = useSuperAdmin();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryKit, setRecoveryKit] = useState(null);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  React.useEffect(() => {
    if (!loading && !recoveryKit) {
      if (setupComplete && !authenticated) {
        navigate("/super-admin/login", { replace: true });
      } else if (authenticated) {
        navigate("/super-admin", { replace: true });
      }
    }
  }, [loading, setupComplete, authenticated, recoveryKit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || username.trim().length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setBusy(true);
    try {
      const data = await setup(username.trim(), email.trim(), password);
      toast.success("Master Super Admin account initialized!");
      if (data?.recovery_kit) {
        setRecoveryKit(data.recovery_kit);
      } else {
        navigate("/super-admin", { replace: true });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Setup failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const formatRecoveryKitText = () => {
    if (!recoveryKit) return "";
    return [
      "============================================================",
      "  RENTAL TRACKER — SUPER ADMIN EMERGENCY RECOVERY KIT",
      "============================================================",
      `Username: ${username.trim()}`,
      `Email:    ${email.trim() || "(not set)"}`,
      `Created:  ${new Date().toISOString()}`,
      "",
      "1. MASTER RECOVERY KEY (Reusable Factor 2 for Password Reset):",
      `   ${recoveryKit.master_recovery_key}`,
      "",
      "2. SINGLE-USE BACKUP CODES (8):",
      ...(recoveryKit.backup_codes || []).map((c, idx) => `   ${idx + 1}. ${c}`),
      "",
      "Keep this file in a secure password manager or offline vault.",
      "============================================================",
    ].join("\n");
  };

  const handleCopyKit = () => {
    navigator.clipboard.writeText(formatRecoveryKitText());
    toast.success("Emergency Recovery Kit copied to clipboard");
  };

  const handleDownloadKit = () => {
    const blob = new Blob([formatRecoveryKitText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rental-tracker-superadmin-recovery-kit.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Downloaded Emergency Recovery Kit (.txt)");
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex flex-col justify-between font-sans selection:bg-teal-500 selection:text-white">
      {/* Top Banner */}
      <div className="border-b border-slate-800/80 bg-[#0E1526]/60 backdrop-blur px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-teal-500 to-emerald-400 flex items-center justify-center shadow-lg shadow-teal-500/20">
            <ShieldAlert className="w-5 h-5 text-slate-950 font-bold" />
          </div>
          <div>
            <div className="text-[11px] font-bold tracking-[0.25em] text-teal-400 uppercase">
              First-Time Setup
            </div>
            <div className="text-sm font-semibold tracking-wide text-white">
              Initialize Master Platform Administrator
            </div>
          </div>
        </div>

        <Link
          to="/login"
          className="text-xs text-slate-400 hover:text-white transition-colors"
        >
          Cancel & Return to App
        </Link>
      </div>

      {/* Main Center */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="bg-[#111827]/90 border border-slate-800/80 rounded-2xl p-8 shadow-2xl backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-emerald-400 to-cyan-500" />

            {!recoveryKit ? (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 text-teal-400 mb-3 shadow-inner">
                    <KeyRound className="w-6 h-6" />
                  </div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    Create Master Administrator
                  </h1>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                    Welcome to Rental Tracker Multi-Tenant Core. No master administrator has been provisioned yet. Set up the root credentials to manage mail servers, tenant isolation, and telemetry.
                  </p>
                </div>

                <div className="mb-6 p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 space-y-2">
                  <div className="flex items-center gap-2 text-teal-400 font-semibold">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span>Super Admin Privileges</span>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-slate-400 pl-1 text-[11px]">
                    <li>Platform-wide SMTP configuration with tenant fallback</li>
                    <li>Monitor tenant database sizes, accounts, and active users</li>
                    <li>Real-time application error logs with full stack traces</li>
                    <li>Emergency maintenance switch & 1-click disaster recovery backups</li>
                  </ul>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                      Super Admin Username
                    </label>
                    <input
                      type="text"
                      required
                      autoFocus
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="e.g. root or sysadmin"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                      Admin Notification & Recovery Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@platform.example"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                        Password (min 6)
                      </label>
                      <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••••"
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••••••"
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={busy}
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold text-sm tracking-wide shadow-lg shadow-teal-500/25 flex items-center justify-center gap-2 transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                          Initializing...
                        </span>
                      ) : (
                        <>
                          <span>Complete Setup & Generate Recovery Kit</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="space-y-5">
                <div>
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-400 mb-3">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    Save Your Emergency Recovery Kit
                  </h2>
                  <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                    To protect Mission Control against unauthorized password resets, resetting a forgotten Super Admin password requires <strong>both</strong> an Email OTP and your <strong>Master Recovery Key</strong> (or one of the 8 Backup Codes below).
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-950 border border-amber-500/30 space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400">
                    Master Recovery Key (Reusable)
                  </div>
                  <div className="font-mono text-sm font-bold text-white tracking-wider select-all bg-slate-900 px-3 py-2 rounded-lg border border-slate-800">
                    {recoveryKit.master_recovery_key}
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Single-Use Backup Recovery Codes (8)
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(recoveryKit.backup_codes || []).map((code) => (
                      <div
                        key={code}
                        className="font-mono text-xs text-teal-300 bg-slate-900/90 border border-slate-800 rounded-lg py-1.5 px-2 text-center select-all"
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={handleCopyKit}
                    className="flex-1 py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white flex items-center justify-center gap-2 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5 text-teal-400" />
                    <span>Copy Recovery Kit</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadKit}
                    className="flex-1 py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white flex items-center justify-center gap-2 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5 text-amber-400" />
                    <span>Download .TXT</span>
                  </button>
                </div>

                <label className="flex items-center gap-2.5 text-xs text-slate-300 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={savedConfirmed}
                    onChange={(e) => setSavedConfirmed(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-900 text-teal-500 focus:ring-teal-500"
                  />
                  <span>I have stored my Master Recovery Key and Backup Codes in a safe place.</span>
                </label>

                <button
                  type="button"
                  disabled={!savedConfirmed}
                  onClick={() => navigate("/super-admin", { replace: true })}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold text-sm tracking-wide shadow-lg shadow-teal-500/25 flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span>Enter Mission Control Console</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center text-xs text-slate-600 border-t border-slate-900">
        Rental Tracker Platform Operations • Super Admin Mission Control
      </div>
    </div>
  );
}
